import {getAnonymizedRoute} from './routeInfo';

export type BrowserNavigationType =
  | 'navigate'
  | 'reload'
  | 'back_forward'
  | 'prerender'
  | 'unknown';
export type RouterNavigationType = 'POP' | 'PUSH' | 'REPLACE' | 'unknown';
export type ReferrerKind = 'none' | 'same_origin' | 'external_origin' | 'unknown';

export type NavigationContext = {
  browser_navigation_type: BrowserNavigationType;
  router_navigation_type: RouterNavigationType;
  referrer_kind: ReferrerKind;
  referrer_route?: string;
};

let currentRouterNavigationType: RouterNavigationType = 'unknown';

const isBrowserNavigationType = (value: unknown): value is BrowserNavigationType =>
  value === 'navigate' || value === 'reload' || value === 'back_forward' || value === 'prerender';

const getBrowserNavigationType = (): BrowserNavigationType => {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
    return 'unknown';
  }

  const navigationEntry = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  return isBrowserNavigationType(navigationEntry?.type) ? navigationEntry.type : 'unknown';
};

const getRouterNavigationType = (value?: string): RouterNavigationType => {
  if (value === 'POP' || value === 'PUSH' || value === 'REPLACE') return value;
  return 'unknown';
};

export const updateRouterNavigationType = (value?: string) => {
  currentRouterNavigationType = getRouterNavigationType(value);
};

const getReferrerContext = (): Pick<NavigationContext, 'referrer_kind' | 'referrer_route'> => {
  if (typeof document === 'undefined' || !document.referrer) {
    return {referrer_kind: 'none'};
  }

  try {
    const referrer = new URL(document.referrer);
    if (typeof window === 'undefined' || referrer.origin !== window.location.origin) {
      return {referrer_kind: 'external_origin'};
    }

    return {
      referrer_kind: 'same_origin',
      referrer_route: getAnonymizedRoute(referrer.pathname),
    };
  } catch {
    return {referrer_kind: 'unknown'};
  }
};

export const getNavigationContext = (routerNavigationType?: string): NavigationContext => ({
  browser_navigation_type: getBrowserNavigationType(),
  router_navigation_type:
    routerNavigationType === undefined
      ? currentRouterNavigationType
      : getRouterNavigationType(routerNavigationType),
  ...getReferrerContext(),
});
